import { useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, XCircle } from "lucide-react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import modalStyles from "../../styles/modals.module.css";
import type {
  VerifastChecklistSummaryVariant,
  VerifastDetailField,
  VerifastModalSection,
} from "../../utils/verifastChecksMapper";

interface VerifastChecksDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: VerifastModalSection[];
}

const CHECKS_TITLE_CLASS: Record<VerifastChecklistSummaryVariant, string> = {
  complete: "text-emerald-800",
  warning: "text-amber-700",
  failed: "text-rose-700",
};

const CHECKS_SUMMARY_CLASS: Record<VerifastChecklistSummaryVariant, string> = {
  complete: "text-emerald-700",
  warning: "text-amber-700",
  failed: "text-rose-700",
};

function BooleanStatusIcon({ value }: { value?: boolean | null }) {
  if (value === true) {
    return (
      <CheckCircle2
        className="h-4 w-4 shrink-0 text-emerald-600"
        aria-label="Passed"
      />
    );
  }
  if (value === false) {
    return (
      <XCircle
        className="h-4 w-4 shrink-0 text-rose-500"
        aria-label="Failed"
      />
    );
  }
  return <span className="text-sm text-slate-500">N/A</span>;
}

function CheckResultIcon({ passed }: { passed: boolean }) {
  if (passed) {
    return (
      <CheckCircle2
        className="h-4 w-4 shrink-0 text-emerald-600"
        aria-label="Passed"
      />
    );
  }
  return (
    <XCircle
      className="h-4 w-4 shrink-0 text-rose-500"
      aria-label="Failed"
    />
  );
}

function DetailFieldRow({ field }: { field: VerifastDetailField }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2.5">
      <span className="text-sm font-medium text-[#2c5282]">{field.label}</span>
      {field.booleanValue !== undefined ? (
        <BooleanStatusIcon value={field.booleanValue} />
      ) : (
        <span className="text-sm text-slate-500">{field.value}</span>
      )}
    </div>
  );
}

function ChecksSummaryIcon({ variant }: { variant: VerifastChecklistSummaryVariant }) {
  if (variant === "failed") {
    return <XCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden />;
  }
  if (variant === "warning") {
    return <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />;
  }
  return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />;
}

function ChecklistItemRow({
  label,
  optional,
  passed,
}: {
  label: string;
  optional: boolean;
  passed: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <span className="text-sm text-slate-800">
        {label}
        {optional ? "*" : ""}
      </span>
      <CheckResultIcon passed={passed} />
    </div>
  );
}

export function VerifastChecksDetailModal({
  isOpen,
  onClose,
  sections,
}: VerifastChecksDetailModalProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="ID Verification Details"
      onClose={onClose}
      maxWidth="max-w-3xl"
      bodyScrollOnly
      headerVariant="centeredTitle"
      headerClassName={modalStyles.modalShellHeaderWhite}
      overlayClassName="!z-[10002]"
      overlayDataTestId="verifast-checks-detail-modal"
      panelDataTestId="verifast-checks-detail-panel"
      titleDataTestId="verifast-checks-detail-title"
      closeButtonDataTestId="verifast-checks-detail-close-button"
    >
      <div className="flex flex-col gap-4">
        {sections.map((section) => {
          const expanded = expandedSections[section.title] ?? false;
          const expandLabel =
            section.type === "details" ? "Expand to view details" : "Expand to view all checks";

          if (section.type === "details") {
            return (
              <section
                key={section.title}
                className="overflow-hidden rounded-lg border border-slate-200 bg-white"
              >
                <div className="border-b border-slate-200 px-4 py-3">
                  <h3 className="text-base font-semibold text-[#1a365d]">
                    {section.title}
                  </h3>
                </div>

                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-[#1597ff] hover:bg-slate-50"
                  onClick={() => toggleSection(section.title)}
                  aria-expanded={expanded}
                >
                  {expanded ? (
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  )}
                  {expanded ? "Collapse details" : expandLabel}
                </button>

                {expanded ? (
                  <div className="grid gap-4 px-4 pb-4 md:grid-cols-2">
                    {section.columns.map((column) => (
                      <div key={column.title} className="min-w-0">
                        <h4 className="mb-3 text-base font-semibold text-[#1a365d]">
                          {column.title}
                        </h4>
                        <div className="flex flex-col gap-2">
                          {column.fields.map((field) => (
                            <DetailFieldRow key={field.key} field={field} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            );
          }

          const variant = section.summaryVariant;
          const previewItems =
            !expanded && section.failedRequiredPreview.length > 0
              ? section.failedRequiredPreview
              : [];

          return (
            <section
              key={section.title}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                <h3
                  className={`text-base font-semibold ${CHECKS_TITLE_CLASS[variant]}`}
                >
                  {section.title}
                </h3>
                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-medium ${CHECKS_SUMMARY_CLASS[variant]}`}
                >
                  <ChecksSummaryIcon variant={variant} />
                  {section.summaryMessage}
                </span>
              </div>

              {previewItems.length > 0 ? (
                <div className="grid gap-2 border-b border-slate-100 px-4 py-3 sm:grid-cols-2">
                  {previewItems.map((item) => (
                    <ChecklistItemRow
                      key={item.key}
                      label={item.label}
                      optional={item.optional}
                      passed={item.passed}
                    />
                  ))}
                </div>
              ) : null}

              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-[#1597ff] hover:bg-slate-50"
                onClick={() => toggleSection(section.title)}
                aria-expanded={expanded}
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                )}
                {expanded ? "Collapse checks" : expandLabel}
              </button>

              {expanded ? (
                <div className="grid gap-2 px-4 pb-4 sm:grid-cols-2">
                  {section.items.map((item) => (
                    <ChecklistItemRow
                      key={item.key}
                      label={item.label}
                      optional={item.optional}
                      passed={item.passed}
                    />
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}

        {sections.some((section) => section.type === "checks") ? (
          <p className={modalStyles.verifastChecksNonMandatoryNote}>
            *Checks marked with an asterisk are non-mandatory.
          </p>
        ) : null}
      </div>
    </OnScreenModal>
  );
}
